// A bill line priced at its own discount. The stock API (/v1/inventory/stock/
// items) takes a discount sent with the request (typed in the entry row, or
// moved to the item's min / max) on MRP for the discount % and amount, yet
// still prices the line, its selling price and tax, at the price list. A line
// whose list price does not carry its discount is priced here instead, the way
// the header discount prices one: MRP × qty less the discount, tax inclusive.

// The API rounds the list discount to 2 decimals and the unit MRP to paise, so
// a line at its list price can miss MRP less its discount by up to a paisa,
// or by 0.01% of MRP on dearer items.
const PAISA = 0.01;
const LIST_ROUNDING_SHARE = 0.0001;

const toAmount = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

const listPriceCarriesDiscount = (unitMrp, unitSellingPrice, discountPer) =>
  Math.abs(unitMrp * (1 - discountPer / 100) - unitSellingPrice) <=
  Math.max(PAISA, unitMrp * LIST_ROUNDING_SHARE);

/**
 * The line's amounts at its discount on MRP. Unit MRP and list price are per
 * unit of `quantity`.
 * @returns { totalAmount, discountAmount, taxAmount, priceWithoutTax }, or null
 *   when the list price already carries the discount (or there is no MRP) and
 *   the API's amounts stand.
 */
export function priceLineAtDiscount({ unitMrp, unitSellingPrice, quantity, discountPer, taxPer }) {
  const mrp = parseFloat(unitMrp) || 0;
  const discount = Math.min(100, Math.max(0, parseFloat(discountPer) || 0));
  if (mrp <= 0 || listPriceCarriesDiscount(mrp, parseFloat(unitSellingPrice) || 0, discount)) {
    return null;
  }
  const mrpTotal = mrp * (parseFloat(quantity) || 0);
  const totalAmount = toAmount(mrpTotal * (1 - discount / 100));
  const tax = parseFloat(taxPer) || 0;
  const taxAmount = toAmount(totalAmount - (totalAmount * 100) / (100 + tax));
  return {
    totalAmount,
    discountAmount: toAmount(mrpTotal - totalAmount),
    taxAmount,
    priceWithoutTax: toAmount(totalAmount - taxAmount),
  };
}
